import { FC, ReactNode } from 'react';

import { Icon } from '@purpur/library/icon';
import { chevronDown } from '@purpur/library/icon/assets/chevron-down';
import { chevronUp } from '@purpur/library/icon/assets/chevron-up';
import AnimateHeight from 'react-animate-height';

import { useComponentId } from '@telialt/utils/src/hooks';

import { SEARCH_TEST_ID } from '../../test/test-ids';
import { useFacetExpansionContext } from './FacetExpansionProvider';
import { FacetItemTitle, FilterItemExpandButton, FilterItemWrapper } from './FacetItem.styles';

type Props = {
    children: ReactNode;
    id: string;
    label: string;
    facetType?: string;
};

export const FacetItem: FC<Props> = ({ children, id, label, facetType }) => {
    const [expandedFacets, setExpandedFacets] = useFacetExpansionContext();
    const isExpanded = !!expandedFacets[id];

    const componentId = useComponentId();

    const toggleExpand = () => {
        setExpandedFacets((prevState) => ({
            ...prevState,
            [id]: !prevState[id],
        }));
    };

    return (
        <FilterItemWrapper
            data-test={SEARCH_TEST_ID.FACETS_ITEM}
            data-facet-id={id}
            data-facet-type={facetType}
            aria-labelledby={`${id}-${componentId}-button`}
        >
            <FilterItemExpandButton
                id={`${id}-${componentId}-button`}
                type="button"
                onClick={toggleExpand}
                $isExpanded={isExpanded}
                aria-expanded={isExpanded}
                aria-controls={`${id}-${componentId}-content`}
                data-test={SEARCH_TEST_ID.FACETS_ITEM_EXPAND_BUTTON}
            >
                <FacetItemTitle>{label}</FacetItemTitle>
                <Icon svg={isExpanded ? chevronUp : chevronDown} size="md" />
            </FilterItemExpandButton>
            <AnimateHeight
                duration={500}
                height={isExpanded ? 'auto' : 0}
                id={`${id}-${componentId}-content`}
                aria-hidden="false"
            >
                {children}
            </AnimateHeight>
        </FilterItemWrapper>
    );
};
